/**
 * Runtime hazard instance (FS-06 §3.2). Pooled; spawn/cull never allocate. Implements the
 * HazardCollider contract (FS-05 §3.3): zNear/zFar are world-space (origin z already added), which
 * lets the broadphase read them without an extra transform.
 */
import { PLAY_RADIUS } from '../../core/constants';
import type { Poolable } from '../../core/pool';
import type { HazardCollider } from '../../player/collision';
import type { HazardKindDef, HazardParams } from './types';

let NEXT_ID = 1;

export class Hazard implements HazardCollider, Poolable {
  poolIndex = 0;
  id = 0;
  active = false;

  def!: HazardKindDef;
  params: HazardParams = {
    amp: 0,
    freq: 0,
    phase: 0,
    nodeTheta: 0,
    nodeHalfTheta: 0,
    nodeRMin: 0,
    nodeRMax: PLAY_RADIUS,
    polarity: 'rim',
    reach: 1,
    count: 0,
    rate: 0,
    seed: 0,
  };

  z = 0;
  prevZ = 0;
  tLocal = 0;
  targetBeat = 0;
  intensity = 0;

  // Latches (FS-05 R27).
  grazed = false;
  nodeAwarded = false;
  resolved = false;
  lethal = true;
  cleared = false;

  // Dissolve animation for Wavebreaker (0 = solid, 1 = gone).
  dissolve = 0;

  get zNear(): number {
    return this.z + this.def.zNear;
  }
  get zFar(): number {
    return this.z + this.def.zFar;
  }
  get nodeTheta(): number {
    // Moving-node kinds (HelixBraid, ChirpSweep) report their current node position.
    return this.def.nodeThetaAt ? this.def.nodeThetaAt(this.params, this.tLocal) : this.params.nodeTheta;
  }
  get nodeHalfTheta(): number {
    return this.params.nodeHalfTheta;
  }

  signedDistance(x: number, y: number, tLocal: number): number {
    return this.def.signedDistance(this.params, x, y, tLocal);
  }

  /**
   * `lethal = false` spawns a **Phantom** (FS-01 R08): the same geometry, drawn as a hollow
   * outline, which `collision.ts` already downgrades to a harmless pass-through. Onboarding's first
   * sixteen seconds are phantoms, and Wavebreaker turns live hazards into them mid-flight.
   */
  spawn(
    def: HazardKindDef,
    params: HazardParams,
    z: number,
    targetBeat: number,
    intensity: number,
    lethal = true,
  ): void {
    this.id = NEXT_ID++;
    this.def = def;
    this.params.amp = params.amp;
    this.params.freq = params.freq;
    this.params.phase = params.phase;
    this.params.nodeTheta = params.nodeTheta;
    this.params.nodeHalfTheta = params.nodeHalfTheta;
    // Polarity, reach and the node's radial band are as much a part of the hazard as its amplitude
    // (FS-06 §4.15, R38). A copy that stops at `seed` silently spawns every hazard rim-anchored at
    // full reach with a full-radius node — which is to say, without any of it.
    this.params.nodeRMin = params.nodeRMin;
    this.params.nodeRMax = params.nodeRMax;
    this.params.polarity = params.polarity;
    this.params.reach = params.reach;
    this.params.count = params.count;
    this.params.rate = params.rate;
    this.params.seed = params.seed;
    this.z = z;
    this.prevZ = z;
    this.tLocal = 0;
    this.targetBeat = targetBeat;
    this.intensity = intensity;
    this.grazed = false;
    this.nodeAwarded = false;
    this.resolved = false;
    this.lethal = lethal;
    this.cleared = false;
    this.dissolve = 0;
    this.active = true;
  }

  poolReset(): void {
    this.active = false;
    this.lethal = true;
  }
}
